import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { Tooltip } from "@/components/common/Tooltip";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { FilterChip } from "@/components/design/FilterChip";
import { PowerTile } from "@/components/design/PowerTile";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { PowersDataDocument } from "@/graphql/generated/operations";
import { ELEMENT_CODES } from "@/lib/elements";
import { rankLabel } from "@/lib/grow-rank";
import { combineLevels, type ScoreSource } from "@/lib/score-source";
import { POWER_GROUPS, POWER_LIBRARY, TAG_DESCRIPTION_IDS, TAG_LABEL_IDS } from "@/lib/power-library";
import type { PowerTag } from "@/lib/power-library";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";

// Grow · Power Library (workshop snapshot screens/PowerLibrary.jsx).
// All 30 powers grouped periodic-table-style; searchable by name/abbr.
// Scores come from the user's REAL data (PowersData) - switchable between
// Selbstbewertung (selfAssessmentResults) and Smart Analyse
// (smartAnalysisResults); the static `level` mocks in power-library.ts are
// deliberately NOT used here.

export function PowerLibraryPage() {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  // Which evaluation the tiles display - applies to ALL tiles at once.
  const [scoreSource, setScoreSource] = useState<ScoreSource>("smart");
  // Show only powers whose self-assessment is not yet complete, so the user
  // sees at a glance what still needs rating. Pre-activated via ?unrated=1
  // (e.g. the home dashboard's "Unbewertete anzeigen" quick jump).
  const [searchParams] = useSearchParams();
  const [onlyUnrated, setOnlyUnrated] = useState(searchParams.get("unrated") === "1");
  // cache-and-network: a quiz part can give a power its first value, and the
  // tile artwork is gated on having one. Cache-first would hold the old state
  // until a reload.
  const { data } = useQuery(PowersDataDocument, { fetchPolicy: "cache-and-network" });

  // Both result sets are keyed by CreativityElement; translate to the
  // 3-letter power code so the library tiles can look them up.
  const sa = data?.selfAssessmentResults;
  const selfByAbbr = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const r of sa ?? []) {
      const code = ELEMENT_CODES[String(r.element)];
      if (code) m.set(code, r.result);
    }
    return m;
  }, [sa]);
  const sm = data?.smartAnalysisResults;
  const smartByAbbr = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const r of sm ?? []) {
      const code = ELEMENT_CODES[String(r.element)];
      if (code) m.set(code, r.score);
    }
    return m;
  }, [sm]);
  // Combined lens: see combineLevels - the same rule the profile's Powers box applies.
  const combinedByAbbr = useMemo(() => combineLevels(selfByAbbr, smartByAbbr), [selfByAbbr, smartByAbbr]);

  const levelByAbbr =
    scoreSource === "self" ? selfByAbbr : scoreSource === "smart" ? smartByAbbr : combinedByAbbr;

  // Badge (medal) rule mirrors discovery: shown when the self-assessment for
  // that power is fully answered (answeredQuestionCount === questionCount).
  // Independent of the score-source toggle.
  const completedByAbbr = useMemo(() => {
    const s = new Set<string>();
    for (const r of sa ?? []) {
      const code = ELEMENT_CODES[String(r.element)];
      if (code && r.questionCount > 0 && r.answeredQuestionCount === r.questionCount) s.add(code);
    }
    return s;
  }, [sa]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return POWER_LIBRARY.filter((p) => {
      if (onlyUnrated && completedByAbbr.has(p.abbr)) return false;
      if (!q) return true;
      return (
        intl.formatMessage({ id: p.nameId }).toLowerCase().includes(q) ||
        p.abbr.toLowerCase().includes(q)
      );
    });
  }, [query, onlyUnrated, completedByAbbr, intl]);

  // Global grow score: share of all self-assessment questions answered across
  // every element (genesis GrowRankingController), paired with the earned rank.
  const ranking = data?.growRanking;

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption). NOTE @adr SPARK-0018-bff-proxies-agent-service-as-second-upstream@1: the assistant is integrated with
  // SELF-ASSESSMENT scores ONLY — never surface the smart/combined values here (that would defeat
  // the powerstats+GrowthAdvisor evaluation), regardless of which source the user is viewing.
  const selfScoreLine = useMemo(() => {
    if (!data) return null;
    return POWER_LIBRARY.map((p) => {
      const v = selfByAbbr.get(p.abbr);
      return `${intl.formatMessage({ id: p.nameId })} ${typeof v === "number" ? v : "unrated"}`;
    }).join("; ");
  }, [data, selfByAbbr, intl]);
  useAssistantContext(
    data
      ? [
          {
            description: "Power Map view state",
            value:
              `overall grow score ${ranking?.score ?? "n/a"}%; ` +
              `${completedByAbbr.size}/${POWER_LIBRARY.length} powers fully self-assessed` +
              (onlyUnrated ? "; filtered to unrated only" : "") +
              (query ? `; search "${query}"` : ""),
          },
          {
            description: "the user's self-assessment power scores (0–100 or unrated; the only power scores available to you)",
            value: selfScoreLine ?? "",
          },
        ]
      : null,
  );

  // Guided tour (/tour): the Power Map's controls and the tile grid, self-assessment framing
  // only (@adr SPARK-0018-bff-proxies-agent-service-as-second-upstream@1 — never mention smart/combined values).
  useAssistantTour(
    data
      ? {
          label: intl.formatMessage({
            id: "grow.powerLibrary.tour.label",
            defaultMessage: "Grow Map",
          }),
          summary: intl.formatMessage(
            {
              id: "grow.powerLibrary.tour.summary",
              defaultMessage:
                "This is the Power Map: all {total} powers, periodic-table style. You have fully self-assessed {completed} of {total} so far.",
            },
            { completed: completedByAbbr.size, total: POWER_LIBRARY.length },
          ),
          stops: [
            {
              anchor: "grow.map.scoreSource",
              title: intl.formatMessage({
                id: "grow.powerLibrary.tour.scoreSource.title",
                defaultMessage: "Score source",
              }),
              description: intl.formatMessage({
                id: "grow.powerLibrary.tour.scoreSource.desc",
                defaultMessage:
                  "Switch which evaluation the numbers on all tiles show. Pick Self-assessment to see the scores from your own ratings.",
              }),
            },
            {
              anchor: "grow.map.filters",
              title: intl.formatMessage({
                id: "grow.powerLibrary.tour.filters.title",
                defaultMessage: "Search & filter",
              }),
              description: intl.formatMessage(
                {
                  id: "grow.powerLibrary.tour.filters.desc",
                  defaultMessage:
                    "Search powers by name or abbreviation, or tap Unrated to see only the {unrated} powers whose self-assessment you haven't completed yet.",
                },
                { unrated: POWER_LIBRARY.length - completedByAbbr.size },
              ),
            },
            {
              anchor: "grow.map.grid",
              title: intl.formatMessage({
                id: "grow.powerLibrary.tour.grid.title",
                defaultMessage: "The power tiles",
              }),
              description: intl.formatMessage(
                {
                  id: "grow.powerLibrary.tour.grid.desc",
                  defaultMessage:
                    "All {total} powers grouped into {clusters} clusters. Click a tile to open that power. A medal and its artwork appear once its self-assessment is complete; {completed} earned so far.",
                },
                {
                  total: POWER_LIBRARY.length,
                  clusters: POWER_GROUPS.length,
                  completed: completedByAbbr.size,
                },
              ),
            },
          ],
        }
      : null,
  );

  return (
    <div
      className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="PowersData"
      data-assistant-anchor="grow.powerMap"
    >
      <div className="mb-5 flex items-baseline justify-between gap-3">
        <BackButton />
      </div>

      <div className="flex items-end justify-between gap-6">
        <div className="min-w-0 flex-1">
          <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/45">
            <FormattedMessage id="grow.powerLibrary.eyebrow" defaultMessage="Power Map" />
          </div>
          <h1 className="font-serif text-5xl leading-tight">
            <FormattedMessage
              id="grow.powerLibrary.heading"
              defaultMessage="{count} Powers"
              values={{ count: POWER_LIBRARY.length }}
            />
          </h1>
          <p className="mt-3 max-w-xl text-sm text-ink/55">
            <FormattedMessage
              id="grow.powerLibrary.subheading"
              defaultMessage="The complete vocabulary: {clusters} clusters, {powers} powers."
              values={{ clusters: POWER_GROUPS.length, powers: POWER_LIBRARY.length }}
            />
          </p>
          {/* Lens — switches which evaluation ALL tiles display.
              Conceptually different from the filters below: this re-interprets
              the numbers, it doesn't reduce the set. Sits on its own row. */}
          <div
            className="mt-8 flex flex-wrap items-center gap-2"
            data-assistant-anchor="grow.map.scoreSource"
          >
            <span className="field-label text-ink/70">
              <FormattedMessage id="grow.powerLibrary.powerScore" defaultMessage="Power Score" />
            </span>
            <SegmentedControl<ScoreSource>
              value={scoreSource}
              onChange={setScoreSource}
              options={[
                {
                  value: "smart",
                  label: intl.formatMessage({
                    id: "grow.powerLibrary.sourceSmart",
                    defaultMessage: "Smart Analysis",
                  }),
                },
                {
                  value: "self",
                  label: intl.formatMessage({
                    id: "grow.powerLibrary.sourceSelf",
                    defaultMessage: "Self-assessment",
                  }),
                },
                {
                  value: "combined",
                  label: intl.formatMessage({
                    id: "grow.powerLibrary.sourceCombined",
                    defaultMessage: "Combined",
                  }),
                },
              ]}
            />
          </div>

          {/* Filter row — search + status chips both narrow the visible set. */}
          <div
            className="mt-4 flex flex-wrap items-center gap-3"
            data-assistant-anchor="grow.map.filters"
          >
            <div className="flex w-full max-w-[14rem] items-center gap-2 rounded-full bg-transparent px-4 py-1.5 ring-1 ring-ink/15 transition focus-within:ring-ink/30">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "grow.powerLibrary.searchPlaceholder",
                  defaultMessage: "Search by name or abbreviation",
                })}
                className="flex-1 bg-transparent text-[12px] text-ink placeholder:text-ink/45 focus:outline-none"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label={intl.formatMessage({
                    id: "grow.powerLibrary.clearSearch",
                    defaultMessage: "Clear search",
                  })}
                  className="shrink-0 text-sm text-ink/40 hover:text-ink"
                >
                  ✕
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <FilterChip active={!onlyUnrated} onClick={() => setOnlyUnrated(false)}>
                <FormattedMessage id="grow.powerLibrary.all" defaultMessage="All" />
              </FilterChip>
              <FilterChip active={onlyUnrated} onClick={() => setOnlyUnrated(true)}>
                <FormattedMessage id="grow.powerLibrary.onlyUnrated" defaultMessage="Unrated" />
              </FilterChip>
            </div>
          </div>
        </div>
        {/* Right column — one object, not two: the profile-progress number and
            the assistant hand-off that acts on it. The number alone is a dead end
            ("0 %, and now what?"), so the hand-off lives in the card's foot and
            the card stops being a read-only verdict. The generic assistant door
            stays the topbar launcher; this one is specific and earned. */}
        <div className="box-flat shrink-0 bg-surface px-5 pb-2 pt-3 text-center">
          {ranking ? (
            <Tooltip
              placement="left"
              content={intl.formatMessage({
                id: "grow.powerLibrary.profileProgressTooltip",
                defaultMessage:
                  "Answer all questions about the powers to increase your profile progress.",
              })}
            >
              <div className="cursor-help whitespace-nowrap">
                <div className="field-label text-ink/55">
                  <FormattedMessage
                    id="grow.powerLibrary.profileProgress"
                    defaultMessage="Profile progress"
                  />
                </div>
                <div className="mt-1 font-serif text-4xl leading-none tabular-nums">{ranking.score}&thinsp;%</div>
                <div className="mt-1 text-[11px] text-ink/55">{rankLabel(ranking.userRank, intl.locale)}</div>
              </div>
            </Tooltip>
          ) : null}
          {/* Outside the `ranking` guard on purpose: PowersData is one wide query, so a
              single failing field (or the first paint) leaves `data` undefined — and the
              hand-off must not disappear with the number it comments on. */}
          <div className={ranking ? "mt-3 border-t border-ink/5 pt-1" : ""}>
            <AskAssistantButton
              variant="ghost"
              // Randomize among 10 equivalent phrasings (grow.powerLibrary.askIntro.0..9) for variety.
              prompt={() =>
                intl.formatMessage({
                  id: `grow.powerLibrary.askIntro.${Math.floor(Math.random() * 10)}`,
                })
              }
              label={<FormattedMessage id="grow.powerLibrary.askButton" />}
            />
          </div>
        </div>
      </div>

      {/* Periodic-table layout: each of the six genesis clusters sits in its
          own framed block (3 per row on desktop), in the stable POWER_GROUPS
          order. Search acts inside the blocks; empty blocks disappear. */}
      <div
        className="mt-6 grid grid-cols-1 items-start gap-3 @sm:grid-cols-2 @lg:grid-cols-3"
        data-assistant-anchor="grow.map.grid"
      >
        {POWER_GROUPS.map((t) => (
          <ClusterBlock
            key={t}
            tag={t}
            entries={filtered.filter((p) => p.tag === t)}
            levelByAbbr={levelByAbbr}
            badgeByAbbr={completedByAbbr}
          />
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-12 text-center text-ink/55">
          <FormattedMessage
            id="grow.powerLibrary.noResults"
            defaultMessage="No power found for “{query}”."
            values={{ query }}
          />
        </div>
      ) : null}
    </div>
  );
}

// Gap-filler colSpan for the tile grid's trailing row, looked up rather than
// interpolated (`col-span-${n}`) because Tailwind's JIT only picks up literal
// class strings from source. Two parallel tables because `grid-cols-3
// @sm:grid-cols-4` is a container query, not a viewport breakpoint: the
// active column count depends on the ClusterBlock's own rendered width, so
// both possible gaps must be encoded and let CSS pick the one that applies.
const GAP_SPAN_3: readonly string[] = ["", "col-span-1", "col-span-2"];
const GAP_SPAN_SM_4: readonly string[] = ["", "@sm:col-span-1", "@sm:col-span-2", "@sm:col-span-3"];

// Spike (#248): with the stable 5-powers-per-cluster count this always finds
// a gap at both container widths (5 % 3 = 2, 5 % 4 = 1). Under an active
// search filter a cluster can show fewer tiles; if that happens to fill a row
// exactly, the description silently doesn't render at that width -- an
// acceptable trade for a spike, not a regression (there's no description at
// all otherwise).
function clusterGapSpanClassName(count: number): string {
  const gap3 = (3 - (count % 3)) % 3;
  const gap4 = (4 - (count % 4)) % 4;
  return [GAP_SPAN_3[gap3], GAP_SPAN_SM_4[gap4]].filter(Boolean).join(" ");
}

// One framed cluster block of the "periodic table": header with the cluster
// letter (element-symbol style) + name + count, a faint watermark letter, and
// the power tiles inside.
function ClusterBlock({
  tag,
  entries,
  levelByAbbr,
  badgeByAbbr,
}: {
  tag: PowerTag;
  entries: readonly (typeof POWER_LIBRARY)[number][];
  levelByAbbr: ReadonlyMap<string, number | null | undefined>;
  badgeByAbbr: ReadonlySet<string>;
}) {
  const navigate = useNavigate();
  const intl = useIntl();
  if (entries.length === 0) return null;
  return (
    // group/cluster is a NAMED group: PowerTile's own hover-reveal (the tile
    // artwork) uses an unnamed `group`/`group-hover:` pair internally, and an
    // unnamed group here would answer to that same generic selector -- every
    // tile's artwork would pop in together on cluster hover instead of only
    // on that tile's own hover.
    <section className="group/cluster box-flat relative overflow-hidden bg-surface p-3">
      <div className="mb-2 flex items-baseline gap-2">
        <span className="field-meta font-medium">{intl.formatMessage({ id: TAG_LABEL_IDS[tag] })}</span>
        <span className="field-label text-ink/55">{entries.length}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 @sm:grid-cols-4">
        {entries.map((p) => {
          const level = levelByAbbr.get(p.abbr);
          const isMeasured = typeof level === "number";
          // Artwork unlocks as soon as the power has a value from any source -
          // a questionnaire-only score counts. The medal stays stricter: it is
          // the reward for completing the per-element self-assessment.
          const rated = badgeByAbbr.has(p.abbr);
          return (
            <PowerTile
              key={p.abbr}
              tag={p.tag}
              abbr={p.abbr}
              name={intl.formatMessage({ id: p.nameId })}
              // Uniform card style regardless of source/measured - only the
              // number appears/changes when toggling the score source.
              active={false}
              score={isMeasured ? level : null}
              // Medal when the self-assessment for this power is complete.
              badge={rated}
              imageSrc={isMeasured || rated ? `/powers/${p.abbr.toLowerCase()}.jpg` : null}
              onSelect={() => navigate(`/grow/power/${p.abbr}`)}
              selectTitle={intl.formatMessage(
                { id: "grow.powerLibrary.openPower", defaultMessage: "Open {name}" },
                { name: intl.formatMessage({ id: p.nameId }) },
              )}
            />
          );
        })}
        {/* Spike (#248): cluster description fills the tile grid's trailing
            gap and fades in on hover of the cluster -- the header or any
            tile, since :hover bubbles up to .group\/cluster regardless of
            depth. Named group (see the section's className) so this doesn't
            also fire PowerTile's own unnamed group-hover (its artwork reveal).
            Not the scale+blur curve of that artwork reveal (#255): blur-md
            is a 12px radius on 12px text, wider than the glyph strokes, so
            the first frames were an unreadable smear, and animating scale
            promotes the paragraph to its own layer, which bitmap-scales the
            glyphs on top of that. Tailwind compiles group-hover inside
            @media (hover: hover), so on a touch-only iPad the reveal could
            not fire at all; pointer-coarse pins it visible there instead.
            group-focus-within is the keyboard half of the same idiom -- a
            sighted keyboard user tabs to the tiles and never hovers, so the
            copy was as unreachable for them as on the iPad (DocItemRow and
            DocPreviewCard pair the two variants the same way). No
            aria-hidden: wherever this is permanently visible it is visible
            text, and hiding visible copy from assistive tech is a defect. */}
        <p
          className={`flex items-center text-[12px] text-ink/55 opacity-0 transition-opacity duration-[var(--duration-fast)] ease-out group-hover/cluster:opacity-100 group-focus-within/cluster:opacity-100 pointer-coarse:opacity-100 ${clusterGapSpanClassName(entries.length)}`}
        >
          {/* Bold cluster name up front (instead of a repeated "core value of
              this cluster" preamble in the copy) -- reuses TAG_LABEL_IDS
              rather than duplicating the name inside the description string.
              Wrapped in one <span> so name+text stay a single flex item and
              wrap as normal running text; as two direct children of this
              flex <p> they'd sit side by side as separate flex items instead. */}
          <span>
            <strong className="font-semibold">
              {intl.formatMessage({ id: TAG_LABEL_IDS[tag] })}:
            </strong>{" "}
            {intl.formatMessage({ id: TAG_DESCRIPTION_IDS[tag] })}
          </span>
        </p>
      </div>
      {/* Faint element-symbol watermark in the top-right corner. Sized
          (text-2xl) to fit within the clean header band above the tile grid:
          the band is ~28px tall (p-3 top + field-meta line + mb-2), so a
          larger text-4xl mark spilled onto the first tile row, where artwork on
          a rated power swallowed its lower half. At this size the full
          two-letter mark stays on clean surface. pointer-events-none keeps
          clicks passing through. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-2 top-2 z-20 select-none font-serif text-2xl leading-none text-ink/[0.12]"
      >
        {tag}
      </span>
    </section>
  );
}
